import useAuthStore from '@stores/auth.store';
import { MENU_STATE_KEY } from '@constants/local-storage.constant';
import Axios from 'axios';
import {
	consumePostLogoutFlag,
	initKeycloak,
	keycloak,
	markPostLogout,
	persistKeycloakSession,
	resetKeycloakInit,
	resolveKeycloakUser,
	syncTokenToStorage,
} from './keycloak';
import { Menus, UserAuthMenu, UserPermissionsResponse } from '@interfaces/auth.interfaces';

/** Plataforma enviada a Autorizaciones (`idplataforma` en la respuesta). */
const PLATAFORMA_ID = 'suite';
/** Aplicación cuyos menús alimentan el sidebar de Admin Suite. */
const APLICACION_ID = 'admin-suite';

let bootstrapPromise: Promise<boolean> | null = null;

function menusFromPermissions(menus: UserAuthMenu[]): Menus {
	return menus.reduce((acc: Menus, cur) => {
		acc[cur.id] = { name: cur.nombre };
		return acc;
	}, {});
}

export function applyPermissionsResponse(
	data: UserPermissionsResponse,
	setMenus: (menus: Menus) => void
): void {
	if (data?.isError) {
		if (data.error_message === 'El token es invalido.') {
			console.warn(
				'API permisos rechazó el token Keycloak; la sesión OIDC se mantiene activa.'
			);
			return;
		}

		if (data.error_code !== 'error_interno') {
			console.warn('Error de permisos:', data.error_message);
		} else {
			console.warn(
				'Se ha comenzado admin-suite omitiendo un error interno de autorizaciones:',
				data.error_message
			);
		}
		return;
	}

	const entries = Array.isArray(data?.response) ? data.response : [];
	const aplicaciones = entries.flatMap((entry) => entry.permisos?.aplicaciones ?? []);
	const adminSuite = aplicaciones.find((app) => app.id === APLICACION_ID);
	const menusData = adminSuite?.menus;

	if (!menusData?.length) {
		console.warn(
			`No se encontraron menús para la aplicación ${APLICACION_ID} en la respuesta de permisos.`
		);
		setMenus({});
		return;
	}

	setMenus(menusFromPermissions(menusData));
}

async function loadPermissions(token: string, usuario: string): Promise<void> {
	const store = useAuthStore.getState();

	try {
		const { data } = await Axios.post<UserPermissionsResponse>(
			`${process.env.REACT_APP_API_AUTORIZACIONES_SIGO_URL}/permisos/generar`,
			{ usuario, tipo: 'web', plataforma: PLATAFORMA_ID },
			{ headers: { Authorization: `Bearer ${token}` } }
		);
		applyPermissionsResponse(data, store.setMenus);
	} catch (error) {
		console.warn('No se pudieron cargar permisos; sesión Keycloak activa.', error);
	}
}

export function clearAuthSession(): void {
	syncTokenToStorage(null);
	const store = useAuthStore.getState();
	store.setToken(null);
	store.setUser(null);
	store.setMenus({});
	store.setLoading(false);
	localStorage.removeItem(MENU_STATE_KEY);
}

export { markPostLogout };

async function runBootstrap(): Promise<boolean> {
	const store = useAuthStore.getState();
	store.setLoading(true);

	const skipSilentSso = consumePostLogoutFlag();

	try {
		const authenticated = await initKeycloak({ skipSilentSso });

		if (!authenticated || !keycloak.token) {
			clearAuthSession();
			return false;
		}

		syncTokenToStorage(keycloak.token);
		persistKeycloakSession(keycloak);
		store.setToken(keycloak.token);

		const user = await resolveKeycloakUser();
		if (!user) {
			console.error('Keycloak autenticado pero sin claims de usuario');
			clearAuthSession();
			return false;
		}

		store.setUser(user);

		await loadPermissions(
			keycloak.token,
			user.email || user.fullName || keycloak.tokenParsed?.preferred_username || ''
		);

		return true;
	} catch (error) {
		console.error('Error en bootstrap Auth Keycloak', error);
		if (!keycloak.authenticated) {
			clearAuthSession();
		}
		return false;
	} finally {
		store.setLoading(false);
	}
}

/**
 * Inicializa Keycloak. En callback OAuth (#code=) debe completarse antes del render.
 */
export function bootstrapAuth(): Promise<boolean> {
	if (!bootstrapPromise) {
		bootstrapPromise = runBootstrap();
	}
	return bootstrapPromise;
}

export function isAuthBootstrapped(): boolean {
	return bootstrapPromise != null;
}

/** Permite re-inicializar tras logout local si Keycloak no redirige. */
export function resetAuthBootstrap(): void {
	bootstrapPromise = null;
	resetKeycloakInit();
}
